import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

export default function Header() {
  const [isOpen, setIsMenuOpen] = useState(false)
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1200)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1200)
      if (window.innerWidth >= 1200) {
        setIsMenuOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false)
    setIsDropdownOpen(false)
  }, [location])

  const services = [
    { label: 'Renovering af hus', href: '/ydelser/renovering-af-hus' },
    { label: 'Nyt tag', href: '/ydelser/nyt-tag' },
    { label: 'Udskiftning af vinduer', href: '/ydelser/udskiftning-af-vinduer' },
    { label: 'Opbygning af træterrasse', href: '/ydelser/opbygning-af-traeterrasse' },
    { label: 'Totalentreprise', href: '/ydelser/totalentreprise' },
    { label: 'Tømreropgaver', href: '/ydelser/toemreropgaver' },
  ]

  const activeClass = "text-brand-orange font-bold border-b-2 border-brand-orange py-2 uppercase text-xs tracking-wider flex items-center"
  const inactiveClass = "text-brand-dark hover:text-brand-orange font-bold transition-colors py-2 uppercase text-xs tracking-wider flex items-center border-b-2 border-transparent"

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-100 shadow-sm">
      {/* Top bar with quick contact */}
      <div className="bg-[#111111] text-white text-xs py-2.5 px-4 md:px-8 flex flex-wrap items-center justify-between gap-2 border-b border-[#222222]">
        <div className="flex items-center gap-4">
          <a href="tel:+4529678107" className="hover:text-brand-orange transition-colors flex items-center gap-1.5 whitespace-nowrap font-medium">
            <i className="fa-solid fa-phone text-brand-blue"></i>
            +45 2967 8107
          </a>
          <a href="mailto:kontakt@baw-byg.dk" className="hover:text-brand-orange transition-colors flex items-center gap-1.5 whitespace-nowrap font-medium">
            <i className="fa-solid fa-envelope text-brand-blue"></i>
            kontakt@baw-byg.dk
          </a>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-white/95 hidden sm:inline font-medium">
            <i className="fa-solid fa-location-dot text-brand-blue mr-1"></i>
            Øster Kringelvej 7, 8250 Egå
          </span>
          <a href="https://www.facebook.com/bawbyg/" target="_blank" rel="noopener noreferrer" className="hover:text-brand-orange transition-colors" aria-label="Facebook">
            <i className="fa-brands fa-facebook text-sm"></i>
          </a>
        </div>
      </div>

      {/* Main navigation bar */}
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img 
            src="https://assets.auxodev.dk/assets/c68264d9-2499-44ef-9e49-6b5cd7a6c3b2/1007-t-c3-b8mrer.svg" 
            alt="BAW Byg og Konstruktion Logo" 
            className="h-16 md:h-20 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        {isDesktop ? (
          <nav className="flex items-center gap-8">
            <NavLink to="/" className={({ isActive }) => isActive ? activeClass : inactiveClass}>
              Forside
            </NavLink>

            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <div
                className={`flex items-center gap-1 uppercase text-xs tracking-wider py-2 border-b-2 cursor-pointer ${
                  location.pathname.startsWith('/ydelser/') ? 'text-brand-orange font-bold border-brand-orange' : 'text-brand-dark hover:text-brand-orange font-bold border-transparent'
                } transition-colors`}
              >
                Ydelser
                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </div>

              {isDropdownOpen && (
                <div className="absolute left-0 top-full pt-2 w-64 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                  <div className="bg-white border border-slate-100 rounded-none shadow-lg py-2">
                    {services.map((s) => (
                      <NavLink
                        key={s.href}
                        to={s.href}
                        className={({ isActive }) =>
                          `block px-4 py-2.5 text-xs uppercase tracking-wider font-bold transition-colors ${
                            isActive
                              ? 'text-brand-orange bg-brand-orange/5'
                              : 'text-slate-700 hover:bg-brand-orange/5 hover:text-brand-orange'
                          }`
                        }
                      >
                        {s.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/projekter" className={({ isActive }) => isActive ? activeClass : inactiveClass}>
              Projekter
            </NavLink>
            <NavLink to="/om-os" className={({ isActive }) => isActive ? activeClass : inactiveClass}>
              Om os
            </NavLink>
            <NavLink to="/kontakt" className={({ isActive }) => isActive ? activeClass : inactiveClass}>
              Kontakt
            </NavLink>

            {/* CTA Button */}
            <Link
              to="/kontakt"
              className="bg-brand-orange hover:bg-brand-orange-hover text-white font-bold py-3 px-6 rounded-none transition-colors shadow-none text-xs uppercase tracking-wider flex items-center gap-2 cursor-pointer"
            >
              Få et tilbud
              <i className="fa-solid fa-arrow-right text-xs"></i>
            </Link>
          </nav>
        ) : (
          /* Mobile Hamburger Button (< 1200px) */
          <button
            onClick={() => setIsMenuOpen(!isOpen)}
            className="text-brand-dark hover:text-brand-orange p-2 text-xl cursor-pointer transition-colors focus:outline-none"
            aria-label="Menu"
          >
            <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'}`}></i>
          </button>
        )}
      </div>

      {/* Mobile Navigation Menu (< 1200px) */}
      {!isDesktop && isOpen && (
        <div className="bg-white border-t border-slate-100 py-4 px-6 space-y-4 animate-in slide-in-from-top duration-200">
          <div className="flex flex-col gap-3">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `text-xs uppercase tracking-wider font-bold py-1.5 ${isActive ? 'text-brand-orange' : 'text-brand-dark'}`
              }
            >
              Forside
            </NavLink>

            {/* Mobile Services list */}
            <div className="space-y-2">
              <div
                className={`text-xs uppercase tracking-wider font-bold py-1.5 block ${location.pathname.startsWith('/ydelser/') ? 'text-brand-orange' : 'text-brand-dark'}`}
              >
                Ydelser
              </div>
              <div className="pl-4 border-l-2 border-slate-100 flex flex-col gap-2.5">
                {services.map((s) => (
                  <NavLink
                    key={s.href}
                    to={s.href}
                    className={({ isActive }) =>
                      `text-xs uppercase tracking-wider font-bold ${isActive ? 'text-brand-orange' : 'text-slate-600 hover:text-brand-orange'}`
                    }
                  >
                    {s.label}
                  </NavLink>
                ))}
              </div>
            </div>

            <NavLink
              to="/projekter"
              className={({ isActive }) =>
                `text-xs uppercase tracking-wider font-bold py-1.5 ${isActive ? 'text-brand-orange' : 'text-brand-dark'}`
              }
            >
              Projekter
            </NavLink>
            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `text-xs uppercase tracking-wider font-bold py-1.5 ${isActive ? 'text-brand-orange' : 'text-brand-dark'}`
              }
            >
              Om os
            </NavLink>
            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `text-xs uppercase tracking-wider font-bold py-1.5 ${isActive ? 'text-brand-orange' : 'text-brand-dark'}`
              }
            >
              Kontakt
            </NavLink>
          </div>

          <div className="pt-4 border-t border-slate-100">
            <Link
              to="/kontakt"
              className="w-full bg-brand-orange hover:bg-brand-orange-hover text-white font-bold py-3.5 px-6 rounded-none transition-colors flex items-center justify-center gap-2 cursor-pointer shadow-none text-xs uppercase tracking-wider"
            >
              Få et uforpligtende tilbud
              <i className="fa-solid fa-arrow-right text-xs"></i>
            </Link>
          </div>
        </div>
      )}
    </header>
  )
}
